/**
 * The running build's version, and a comparator for it.
 *
 * Split out of `updateLifecycle.ts` for two reasons: `__APP_VERSION__` is a
 * build-time define (Vite replaces the identifier textually, so a test cannot
 * stub it in place), and the comparator is the part of the
 * MIN_SUPPORTED_VERSION force path that most deserves direct tests.
 */

/** Repo VERSION baked in at build time — dotted, e.g. `0.6.5.128`. */
export function runningAppVersion(): string {
  return __APP_VERSION__;
}

/**
 * Build IDENTITY of the running document — the git SHA baked in at build time.
 *
 * Deliberately not a version: this answers "is this the same build?", never "is
 * this newer?". Two deploys can carry the same VERSION, and a rollback deploys an
 * OLDER one that a tab still has to pick up, so ordering is the wrong question and
 * a SHA has no ordering to offer anyway.
 */
export function runningBuildId(): string {
  return __FRONTEND_BUILD_ID__;
}

/** Dotted numeric version: `0`, `1.2`, `0.6.5.128`. Any depth. */
const DOTTED_VERSION = /^\d+(?:\.\d+)*$/;

/**
 * Compare two dotted numeric versions segment by segment.
 *
 * Returns a negative number when `a < b`, zero when equal, positive when
 * `a > b` — and `null` when either side is not a dotted numeric version.
 *
 * `null` rather than a guessed ordering on purpose: string ordering gets
 * `0.6.5.9` vs `0.6.5.128` backwards, and the callers of this decide whether to
 * force-reload a user's tab. An uncomparable pair is a config or build problem
 * that has to surface, not something to paper over (ADR-0007).
 *
 * Missing trailing segments count as zero, so `0.6.5` < `0.6.5.1` and
 * `0.6.5` === `0.6.5.0`.
 */
export function compareDottedVersions(a: string, b: string): number | null {
  if (!DOTTED_VERSION.test(a)) return null;
  if (!DOTTED_VERSION.test(b)) return null;

  const left = a.split('.').map((part) => Number.parseInt(part, 10));
  const right = b.split('.').map((part) => Number.parseInt(part, 10));
  const depth = Math.max(left.length, right.length);

  for (let i = 0; i < depth; i += 1) {
    const l = i < left.length ? left[i] : 0;
    const r = i < right.length ? right[i] : 0;
    if (l !== r) return l - r;
  }
  return 0;
}
